import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Eye, Link2, Copy, Check, ExternalLink } from "lucide-react";
import { PageTitle, Panel, Btn, StatCard } from "../components/ui";
import { useI18n } from "../i18n";
import { CL } from "../api";

export default function Reports() {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => { CL.summary().then(setSummary).catch(() => {}); }, []);

  const withBlob = async (fn) => {
    setBusy(true);
    try {
      const blob = await CL.reportBlob();
      const url = URL.createObjectURL(blob);
      fn(url);
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Rapor oluşturulamadı"); }
    finally { setBusy(false); }
  };

  const download = () => withBlob((url) => {
    const a = document.createElement("a");
    a.href = url; a.download = "karbon-raporu.pdf"; document.body.appendChild(a); a.click(); a.remove();
    toast.success("PDF indirildi");
  });

  const preview = () => withBlob((url) => window.open(url, "_blank"));

  const share = async () => {
    setBusy(true);
    try {
      const { token } = await CL.createShare();
      const url = CL.publicReportUrl(token);
      setShareUrl(url);
      toast.success(t("shareLinkReady"));
    } catch { toast.error("Bağlantı oluşturulamadı"); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); } catch { /* ignore */ }
    setCopied(true); toast.success(t("copied")); setTimeout(() => setCopied(false), 2000);
  };

  const total = summary ? (summary.total / 1000).toFixed(2) : "0.00";

  return (
    <div data-testid="reports-page">
      <PageTitle title={t("reportTitle")} subtitle={t("reportDesc")} />

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard testId="report-total" label={t("totalEmissions")} value={total} sub={t("tCO2e")} />
        <StatCard testId="report-facilities" label={t("facilities")} value={summary?.counts?.facilities ?? 0} />
        <StatCard testId="report-activities" label="Aktivite" value={summary?.counts?.activities ?? 0} accent="#2f8f5b" />
      </div>

      <Panel className="max-w-3xl">
        <div className="flex flex-wrap gap-3">
          <Btn onClick={download} disabled={busy} data-testid="report-download"><FileDown className="h-4 w-4" />{t("downloadPdf")}</Btn>
          <Btn variant="outline" onClick={preview} disabled={busy} data-testid="report-preview"><Eye className="h-4 w-4" />{t("previewPdf")}</Btn>
          <Btn variant="outline" onClick={share} disabled={busy} data-testid="report-share"><Link2 className="h-4 w-4" />{t("createShareLink")}</Btn>
        </div>

        {shareUrl && (
          <div className="mt-6 rounded-xl border border-[#e4e6dd] bg-[#f7f9f3] p-4" data-testid="share-box">
            <div className="mb-2 text-xs font-medium uppercase tracking-wider text-[#8a8f83]">{t("shareLinkReady")}</div>
            <div className="flex flex-wrap items-center gap-2">
              <input readOnly value={shareUrl} data-testid="share-url"
                className="min-w-0 flex-1 rounded-lg border border-[#dcdfd4] bg-white px-3 py-2 text-sm text-[#1b2420]" />
              <Btn onClick={copy} data-testid="share-copy">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{copied ? t("copied") : t("copy")}</Btn>
              <a href={shareUrl} target="_blank" rel="noopener noreferrer" data-testid="share-open"
                className="inline-flex items-center gap-2 rounded-lg border border-[#dcdfd4] bg-white px-4 py-2 text-sm text-[#3a423b] hover:bg-[#f0f2ea]">
                <ExternalLink className="h-4 w-4" />{t("openLink")}
              </a>
            </div>
            <p className="mt-2 text-xs text-[#8a8f83]">{t("shareHint")}</p>
          </div>
        )}
      </Panel>
    </div>
  );
}
